import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { useCreateSalaryRecord } from '../queries';

const schema = z.object({
  effective_date: z.string().min(1, 'Date is required'),
  amount: z.string().refine((v) => v !== '' && Number(v) > 0, 'Enter an amount'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  fx_rate_to_base: z.string().refine((v) => Number(v) > 0, 'Must be > 0'),
  note: z.string().max(255),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['effective_date', 'amount', 'currency', 'fx_rate_to_base', 'note'] as const;

/** Inline "add compensation entry" — new payslips prefill from the latest
 *  record (ADR-048), so keeping this current is what drives payroll. */
export function AddSalaryRecordForm({ employeeId }: { employeeId: number }) {
  const create = useCreateSalaryRecord(employeeId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      effective_date: '',
      amount: '',
      currency: 'USD',
      fx_rate_to_base: '1',
      note: '',
    },
  });

  async function handleSubmit(values: FormValues) {
    try {
      await create.mutateAsync(values);
      form.reset({ ...form.getValues(), effective_date: '', amount: '', note: '' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-wrap items-end gap-3"
      >
        <FormField
          control={form.control}
          name="effective_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Effective from</FormLabel>
              <FormControl>
                <Input type="date" className="w-40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount</FormLabel>
              <FormControl>
                <Input type="number" min={0} step="0.01" className="w-32" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="fx_rate_to_base"
          render={({ field }) => (
            <FormItem>
              <FormLabel>FX → USD</FormLabel>
              <FormControl>
                <Input type="number" min={0} step="0.00000001" className="w-28" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem className="min-w-44 flex-1">
              <FormLabel>Note</FormLabel>
              <FormControl>
                <Input placeholder="Annual raise…" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="sm" disabled={create.isPending}>
          <Plus /> {create.isPending ? 'Adding…' : 'Add record'}
        </Button>
        {form.formState.errors.root ? (
          <p className="text-destructive w-full text-sm">{form.formState.errors.root.message}</p>
        ) : null}
      </form>
    </Form>
  );
}
